<template>
  <div class="mail">
    <div class="mail-header-row">
      <input type="text" v-model="searchQuery" class="search-bar"
        :placeholder="$t('customerMail.search.placeholder')" />
      <div class="mail-controls">
        <button class="filter-btn">{{ $t('customerMail.controls.filter') }} <i class="fas fa-chevron-down"></i></button>
        <button class="select-mail-btn">{{ $t('customerMail.controls.selectMail') }} <i class="fas fa-chevron-down"></i></button>
        <button class="view-toggle-btn" :class="{ active: viewMode === 'list' }" @click="setViewMode('list')"
          aria-label="List View"><i class="fas fa-list"></i></button>
        <button class="view-toggle-btn" :class="{ active: viewMode === 'grid' }" @click="setViewMode('grid')"
          aria-label="Grid View"><i class="fas fa-th"></i></button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i>
      {{ $t('customerMail.loading') }}
    </div>

    <!-- Empty State -->
    <div v-else-if="mailItems.length === 0" class="empty-state">
      <i class="fas fa-inbox"></i>
      <p>{{ $t('customerMail.empty.title') }}</p>
    </div>

    <!-- Content -->
    <template v-else>
      <!-- List View -->
      <div v-if="viewMode === 'list'" class="mail-table-section">
        <table class="mail-table">
          <thead>
            <tr>
              <th><input type="checkbox" v-model="selectAll" @change="toggleSelectAll" /></th>
              <th>{{ $t('customerMail.table.headers.id') }}</th>
              <th>{{ $t('customerMail.table.headers.type') }}</th>
              <th>{{ $t('customerMail.table.headers.priority') }}</th>
              <th>{{ $t('customerMail.table.headers.receivedDate') }}</th>
              <th>{{ $t('customerMail.table.headers.status') }}</th>
              <th>{{ $t('customerMail.table.headers.tracking') }}</th>
              <th>{{ $t('customerMail.table.headers.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="mail in paginatedMail" :key="mail.id">
              <td><input type="checkbox" v-model="selectedMails" :value="mail.id" /></td>
              <td>{{ mail.displayId }}</td>
              <td>{{ mail.type }}</td>
              <td>
                <span :class="['priority-badge', mail.priority.toLowerCase()]">
                  {{ mail.priority }}
                </span>
              </td>
              <td>{{ formatDate(mail.date) }}</td>
              <td>
                <span :class="['status-badge', mail.status]">
                  {{ $t(`customerMail.status.${mail.status.toLowerCase()}`) }}
                </span>
              </td>
              <td>{{ mail.trackingNumber || 'N/A' }}</td>
              <td>
                <div class="action-buttons">
                  <v-btn
                    color="primary"
                    size="small"
                    @click="viewMail(mail)"
                    class="action-btn"
                  >
                    {{ $t('customerMail.actions.view') }}
                  </v-btn>
                  <template v-if="mail.status === 'Scanned'">
                    <v-btn
                      color="success"
                      size="small"
                      @click="forwardMail(mail)"
                      class="action-btn"
                    >
                      {{ $t('customerMail.actions.forward') }}
                    </v-btn>
                    <v-btn
                      color="error"
                      size="small"
                      @click="shredMail(mail)"
                      class="action-btn"
                    >
                      {{ $t('customerMail.actions.shred') }}
                    </v-btn>
                  </template>
                  <template v-if="mail.status === 'Received'">
                    <v-btn
                      color="info"
                      size="small"
                      @click="requestScan(mail)"
                      class="action-btn"
                    >
                      {{ $t('customerMail.actions.scanRequest') }}
                    </v-btn>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Grid View -->
      <div v-else class="mail-grid-section">
        <div class="mail-grid">
          <div class="mail-card" v-for="mail in paginatedMail" :key="mail.id">
            <div class="mail-card-header">
              <span class="mail-id">#{{ mail.displayId }}</span>
              <span :class="['status-badge', mail.status]">{{ $t(`customerMail.status.${mail.status.toLowerCase()}`) }}</span>
            </div>
            <div class="mail-card-body">
              <div class="mail-info-row">
                <strong>{{ $t('customerMail.table.headers.type') }}:</strong> {{ mail.type }}
                <span :class="['priority-badge', mail.priority.toLowerCase()]">
                  {{ mail.priority }}
                </span>
              </div>
              <div class="mail-info-row">
                <strong>{{ $t('customerMail.table.headers.receivedDate') }}:</strong> {{ formatDate(mail.date) }}
              </div>
              <div class="mail-info-row">
                <strong>{{ $t('customerMail.table.headers.tracking') }}:</strong> {{ mail.trackingNumber || 'N/A' }}
              </div>
              <div v-if="mail.description" class="mail-info-row">
                <strong>{{ $t('customerMail.table.headers.description') }}:</strong> {{ mail.description }}
              </div>
            </div>
            <div class="mail-card-actions">
              <div class="action-buttons">
                <v-btn
                  color="primary"
                  size="small"
                  @click="viewMail(mail)"
                  class="action-btn"
                >
                  {{ $t('customerMail.actions.view') }}
                </v-btn>
                <template v-if="mail.status === 'Scanned'">
                  <v-btn
                    color="success"
                    size="small"
                    @click="forwardMail(mail)"
                    class="action-btn"
                  >
                    {{ $t('customerMail.actions.forward') }}
                  </v-btn>
                  <v-btn
                    color="error"
                    size="small"
                    @click="shredMail(mail)"
                    class="action-btn"
                  >
                    {{ $t('customerMail.actions.shred') }}
                  </v-btn>
                </template>
                <template v-if="mail.status === 'Received'">
                  <v-btn
                    color="info"
                    size="small"
                    @click="requestScan(mail)"
                    class="action-btn"
                  >
                    {{ $t('customerMail.actions.scanRequest') }}
                  </v-btn>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="pagination">
        <button :disabled="currentPage === 1" @click="currentPage--">&lt;</button>
        <button v-for="page in totalPages" :key="page" :class="{ active: currentPage === page }"
          @click="goToPage(page)">
          {{ page }}
        </button>
        <span v-if="totalPages > 5">...</span>
        <button :disabled="currentPage === totalPages" @click="currentPage++">&gt;</button>
      </div>
    </template>

    <!-- Attachment Preview Modal -->
    <div v-if="showPreviewModal" class="modal-overlay" @click.self="closePreviewModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2><i class="fas fa-file-alt"></i> {{ $t('customerMail.preview.title') }}</h2>
          <button @click="closePreviewModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <!-- Attachments Sidebar -->
          <div class="attachments-sidebar">
            <div class="attachments-header">
              <h3>
                <i class="fas fa-paperclip"></i>
                {{ $t('customerMail.preview.attachments') }}
                <span v-if="selectedMail && selectedMail.attachments" class="attachments-count">
                  {{ selectedMail.attachments.length }}
                </span>
              </h3>
            </div>
            
            <div class="attachments-list">
              <template v-if="selectedMail && selectedMail.attachments && selectedMail.attachments.length > 0">
                <div v-for="(attachment, index) in selectedMail.attachments" 
                    :key="index"
                    :class="['attachment-item', { active: attachment === previewAttachment }]"
                    @click="showPreview(attachment)">
                  <div class="attachment-item-header">
                    <i :class="getFileIcon(attachment.originalName)"></i>
                    <div class="attachment-details">
                      <span class="attachment-name">{{ attachment.originalName }}</span>
                      <span class="attachment-info">
                        <i class="fas fa-file"></i>
                        {{ attachment.originalName.split('.').pop().toUpperCase() }}
                      </span>
                    </div>
                  </div>
                </div>
              </template>
              <div v-else class="no-attachments">
                <i class="fas fa-file-alt"></i>
                <p>{{ $t('customerMail.preview.noAttachments') }}</p>
              </div>
            </div>
          </div>
          
          <!-- Preview Main Content -->
          <div class="preview-main">
            <div v-if="previewAttachment" class="preview-container">
              <div class="preview-toolbar">
                <div class="preview-title">
                  <i :class="getFileIcon(previewAttachment.originalName)"></i>
                  {{ previewAttachment.originalName }}
                </div>
                <div class="preview-actions">
                  <button v-if="isImageFile(previewAttachment.originalName)" 
                          class="preview-btn" 
                          @click="zoomIn" 
                          :title="$t('customerMail.preview.actions.zoomIn')">
                    <i class="fas fa-search-plus"></i>
                  </button>
                  <button v-if="isImageFile(previewAttachment.originalName)" 
                          class="preview-btn" 
                          @click="zoomOut" 
                          :title="$t('customerMail.preview.actions.zoomOut')">
                    <i class="fas fa-search-minus"></i>
                  </button>
                  <button v-if="isImageFile(previewAttachment.originalName)" 
                          class="preview-btn" 
                          @click="resetZoom" 
                          :title="$t('customerMail.preview.actions.resetZoom')">
                    <i class="fas fa-undo"></i>
                  </button>
                  <button class="preview-btn download"
                          @click="downloadAttachment(previewAttachment)">
                    <i class="fas fa-download"></i>
                    {{ $t('customerMail.preview.actions.download') }}
                  </button>
                </div>
              </div>
              
              <div class="preview-content">
                <!-- Image Preview -->
                <img v-if="isImageFile(previewAttachment.originalName)" 
                     :src="getFileUrl(previewAttachment.path)" 
                     :alt="previewAttachment.originalName"
                     :style="{ transform: `scale(${zoomLevel})` }"
                     class="preview-image"
                     @load="handlePreviewLoad"
                     @error="handleImageError" />
                <!-- PDF Preview -->
                <div v-else-if="isPdfFile(previewAttachment.originalName)" class="pdf-container">
                  <div class="pdf-loading" v-if="isLoading">
                    <i class="fas fa-spinner fa-spin"></i>
                    <p>{{ $t('customerMail.preview.preview.loading') }}</p>
                  </div>
                  <iframe 
                    :src="getFileUrl(previewAttachment.path)"
                    class="preview-pdf"
                    frameborder="0"
                    @load="handlePreviewLoad"
                    @error="handlePdfError"
                    :title="$t('customerMail.preview.preview.title')"></iframe>
                  <div class="pdf-controls" v-if="!isLoading && !previewError">
                    <div class="pdf-pagination">
                      <button class="pdf-nav-btn" @click="previousPage" :disabled="pdfCurrentPage <= 1">
                        <i class="fas fa-chevron-left"></i>
                      </button>
                      <span class="pdf-page-info">Page {{ pdfCurrentPage }} of {{ pdfTotalPages }}</span>
                      <button class="pdf-nav-btn" @click="nextPage" :disabled="pdfCurrentPage >= pdfTotalPages">
                        <i class="fas fa-chevron-right"></i>
                      </button>
                    </div>
                  </div>
                </div>
                
                <!-- Unsupported File Types -->
                <div v-else class="preview-unsupported">
                  <i :class="getFileIcon(previewAttachment.originalName)"></i>
                  <p>{{ $t('customerMail.preview.preview.unsupported') }}</p>
                  <button class="preview-btn download" @click="downloadAttachment(previewAttachment)">
                    <i class="fas fa-download"></i>
                    {{ $t('customerMail.preview.preview.downloadToView') }}
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="preview-placeholder">
              <i class="fas fa-file-alt"></i>
              <p>{{ $t('customerMail.preview.preview.selectFile') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '@/styles/CustomerMail.css'
import incomingMailService from '../services/incomingMailService'

const MailEnums = {
  Status: {
    RECEIVED: 'Received',
    SCAN_REQUESTED: 'Scan_requested',
    SCANNED: 'Scanned',
    FORWARD_REQUESTED: 'Forward_requested',
    FORWARDED: 'Forwarded',
    SHRED_REQUESTED: 'Shred_requested',
    SHREDDED: 'Shredded'
  }
};

export default {
  name: 'CustomerMail',
  props: {
    selectedBusiness: {
      type: Object,
      required: true
    }
  },  data() {
    return {
      searchQuery: '',
      selectAll: false,
      selectedMails: [],
      currentPage: 1,
      pageSize: 10,
      mailItems: [],
      viewMode: 'list',
      loading: false,
      error: null,
      mailTypes: {
        legal: 'Legal',
        tax: 'Tax',
        general: 'General'
      },
      mailPriorities: {
        low: 'Low',
        medium: 'Medium',
        high: 'High',
        urgent: 'Urgent'
      },
      showPreviewModal: false,
      selectedMail: null,
      previewAttachment: null,
      isLoading: false,
      zoomLevel: 1,
      pdfCurrentPage: 1,
      pdfTotalPages: 1,
      previewError: false,
      previewLoadingText: 'Loading preview...'
    }
  },
  watch: {
    selectedBusiness: {
      immediate: true,
      async handler(newBusiness) {
        if (newBusiness && newBusiness.id) {
          await this.fetchBusinessMail(newBusiness.id);
        }
      }
    }
  },
  computed: {
    filteredMail() {
      let filtered = this.mailItems;
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        filtered = filtered.filter(mail =>
          mail.id.toLowerCase().includes(q) ||
          this.mailTypes[mail.mailType]?.toLowerCase().includes(q) ||
          mail.trackingNumber?.toLowerCase().includes(q)
        );
      }
      return filtered;
    },
    paginatedMail() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredMail.slice(start, start + this.pageSize);
    },
    totalPages() {
      return Math.ceil(this.filteredMail.length / this.pageSize);
    }
  },
  methods: {
    async fetchBusinessMail(businessId) {
      this.loading = true;
      this.error = null;
      try {
        const response = await incomingMailService.getBusinessMail(businessId);
        if (response && response.data) {
          this.mailItems = response.data.map(mail => ({
            ...mail,
            type: this.mailTypes[mail.mailType] || mail.mailType,
            priority: this.mailPriorities[mail.priority] || mail.priority,
            date: mail.receivedDate,
            status: mail.status,
            displayId: this.formatMailId(mail.receivedDate)
          }));
        }
      } catch (error) {
        console.error('Error fetching business mail:', error);
        this.error = 'Failed to load mail items';
      } finally {
        this.loading = false;
      }
    },
    formatMailId(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${year}${month}${day}-${hours}${minutes}`;
    },
    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      return d.toLocaleDateString(this.$i18n.locale === 'es' ? 'es-ES' : 'en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    statusLabel(status) {
      return this.$t(`customerMail.status.${status.toLowerCase()}`);
    },
    goToPage(page) {
      this.currentPage = page;
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedMails = this.paginatedMail.map(mail => mail.id);
      } else {
        this.selectedMails = [];
      }
    },
    viewMail(mail) {
      this.selectedMail = mail;
      this.showPreviewModal = true;
      if (mail.attachments && mail.attachments.length > 0) {
        this.showPreview(mail.attachments[0]);
      }
    },    async forwardMail(mail) {
      try {
        // Update the local mail item status immediately
        const mailIndex = this.mailItems.findIndex(m => m.id === mail.id);
        if (mailIndex !== -1) {
          const now = new Date().toISOString();
          this.mailItems[mailIndex] = {
            ...this.mailItems[mailIndex],
            status: MailEnums.Status.FORWARDED,
            lastActionDate: now,
            lastActionBy: 'agent'
          };
        }

        const forwardDetails = {
          businessId: this.selectedBusiness.id,
          mailId: mail.id,
          status: MailEnums.Status.FORWARD_REQUESTED,
          forwardedTo: mail.forwardedTo,
          trackingNumber: mail.forwardedTrackingNumber,
          lastActionDate: new Date().toISOString()
        };
        
        // Call the forward service
        await incomingMailService.forwardMail(mail.id, forwardDetails);
        
        // Show success message
        alert('Mail has been forwarded successfully');
      } catch (error) {
        // If there's an error, revert the status change
        const mailIndex = this.mailItems.findIndex(m => m.id === mail.id);
        if (mailIndex !== -1) {
          this.mailItems[mailIndex] = {
            ...this.mailItems[mailIndex],
            status: MailEnums.Status.SCANNED, // Revert to previous status
            lastActionDate: mail.lastActionDate
          };
        }
        
        console.error('Error forwarding mail:', error);
        alert('Failed to forward mail.');
      }
    },    async shredMail(mail) {
      try {
        // Update the local mail item status immediately
        const mailIndex = this.mailItems.findIndex(m => m.id === mail.id);
        if (mailIndex !== -1) {
          const now = new Date().toISOString();
          this.mailItems[mailIndex] = {
            ...this.mailItems[mailIndex],
            status: MailEnums.Status.SHREDDED,
            lastActionDate: now,
            lastActionBy: 'agent'
          };
        }

        const shredDetails = {
          businessId: this.selectedBusiness.id,
          mailId: mail.id,
          status: MailEnums.Status.SHRED_REQUESTED,
          reason: mail.shreddingReason,
          confirmation: mail.shreddingConfirmation,
          lastActionDate: new Date().toISOString()
        };
        
        // Call the shred service
        await incomingMailService.shredMail(mail.id, shredDetails);
        
        // Show success message
        alert('Mail has been shredded successfully');
      } catch (error) {
        // If there's an error, revert the status change
        const mailIndex = this.mailItems.findIndex(m => m.id === mail.id);
        if (mailIndex !== -1) {
          this.mailItems[mailIndex] = {
            ...this.mailItems[mailIndex],
            status: MailEnums.Status.SCANNED, // Revert to previous status
            lastActionDate: mail.lastActionDate
          };
        }
        
        console.error('Error shredding mail:', error);
        alert('Failed to shred mail.');
      }
    },
    async scanMail(mail) {
      try {
        // Call the scan service
        await incomingMailService.scanMail(mail.id);

        // Update the local mail item status immediately
        const mailIndex = this.mailItems.findIndex(m => m.id === mail.id);
        if (mailIndex !== -1) {
          const now = new Date().toISOString();
          this.mailItems[mailIndex] = {
            ...this.mailItems[mailIndex],
            status: 'Scan_requested',
            lastActionDate: now,
            lastActionBy: 'agent'
          };
        }

        // Show success message
        alert('Scan request submitted successfully');
      } catch (error) {
        console.error('Error scanning mail:', error);
        alert('Failed to submit scan request. Please try again.');
      }
    },
    setViewMode(mode) {
      this.viewMode = mode;
    },
    downloadAttachment(attachment) {
      if (attachment && attachment.path) {
        this.isLoading = true;
        incomingMailService.downloadAttachment(attachment.path)
          .then(() => {
            console.log('Download completed');
          })
          .catch(error => {
            console.error('Error downloading attachment:', error);
            alert('Failed to download attachment. Please try again.');
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    viewAttachments(mail) {
      this.selectedMail = mail;
      this.showPreviewModal = true;
    },
    closePreviewModal() {
      this.showPreviewModal = false;
      this.selectedMail = null;
      this.previewAttachment = null;
      this.zoomLevel = 1;
    },      showPreview(attachment) {
      this.previewAttachment = attachment;
      this.isLoading = true;
      this.previewError = false;
      this.zoomLevel = 1;
      this.pdfCurrentPage = 1;
      this.pdfTotalPages = 1;
      this.updatePreviewTransform();
      
      // Set appropriate loading text based on file type
      if (this.isPdfFile(attachment.originalName)) {
        this.previewLoadingText = 'Loading PDF document...';
      } else if (this.isImageFile(attachment.originalName)) {
        this.previewLoadingText = 'Loading image...';
      } else {
        this.previewLoadingText = 'Loading preview...';
      }
    },
    closePreview() {
      this.previewAttachment = null;
      this.zoomLevel = 1;
    },
    zoomIn() {
      this.zoomLevel = Math.min(3, this.zoomLevel + 0.25);
      this.updatePreviewTransform();
    },
    zoomOut() {
      this.zoomLevel = Math.max(0.5, this.zoomLevel - 0.25);
      this.updatePreviewTransform();
    },
    resetZoom() {
      this.zoomLevel = 1;
      this.updatePreviewTransform();
    },
    updatePreviewTransform() {
      const img = document.querySelector('.preview-image');
      if (img) {
        img.style.transform = `scale(${this.zoomLevel})`;
      }
    },      handlePreviewLoad() {
      // Add a small delay to ensure content is properly loaded
      setTimeout(() => {
        this.isLoading = false;
        this.updatePreviewTransform();
        
        // If it's a PDF, try to detect page count
        if (this.previewAttachment && this.isPdfFile(this.previewAttachment.originalName)) {
          this.previewError = false;
          this.pdfCurrentPage = 1;
          
          // Wait a bit longer for PDF to fully load before trying to detect pages
          setTimeout(() => {
            this.detectPdfPageCount();
          }, 500);
        }
      }, 100);
    },
    getFileIcon(filename) {
      const extension = filename.split('.').pop().toLowerCase();
      const iconMap = {
        pdf: 'fas fa-file-pdf',
        doc: 'fas fa-file-word',
        docx: 'fas fa-file-word',
        xls: 'fas fa-file-excel',
        xlsx: 'fas fa-file-excel',
        jpg: 'fas fa-file-image',
        jpeg: 'fas fa-file-image',
        png: 'fas fa-file-image',
        gif: 'fas fa-file-image',
        txt: 'fas fa-file-alt',
        zip: 'fas fa-file-archive',
        rar: 'fas fa-file-archive',
      };
      return iconMap[extension] || 'fas fa-file';
    },
    isImageFile(filename) {
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
      const extension = filename.split('.').pop().toLowerCase();
      return imageExtensions.includes(extension);
    },
    isPdfFile(filename) {
      return filename.toLowerCase().endsWith('.pdf');
    },
    getFileUrl(path) {
      // Convert backslashes to forward slashes for consistent path handling
      const normalizedPath = path.replace(/\\/g, '/');
      // Get API base URL from environment or use default
      const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:5001';
      // Remove /api from base URL if present, since the files route is not under /api
      const uploadsBaseUrl = baseUrl.replace(/\/api$/, '');
      // Get the relative path after the uploads directory
      const relativePath = normalizedPath.split('Files/').pop();
      return `${uploadsBaseUrl}/files/${relativePath}`;
    },
    handleImageError(event) {
      console.error('Failed to load image:', event);
      event.target.style.display = 'none';
      event.target.parentElement.classList.add('preview-error');
      event.target.parentElement.innerHTML = `
        <div class="preview-error-content">
          <i class="fas fa-exclamation-circle"></i>
          <p>Failed to load image</p>
          <button class="download-btn" @click="downloadAttachment(previewAttachment)">
            <i class="fas fa-download"></i> Download to View
          </button>
        </div>
      `;
    },
    handlePdfError(event) {
      console.error('Failed to load PDF:', event);
      this.previewError = true;
      this.isLoading = false;
      event.target.style.display = 'none';
      event.target.parentElement.classList.add('preview-error');
      event.target.parentElement.innerHTML = `
        <div class="preview-error-content">
          <i class="fas fa-exclamation-circle"></i>
          <p>Failed to load PDF</p>
          <button class="preview-btn download" @click="downloadAttachment(previewAttachment)">
            <i class="fas fa-download"></i> Download to View
          </button>
        </div>
      `;
    },
    nextPage() {
      if (this.pdfCurrentPage < this.pdfTotalPages) {
        this.pdfCurrentPage++;
        this.navigateToPdfPage();
      }
    },
    previousPage() {
      if (this.pdfCurrentPage > 1) {
        this.pdfCurrentPage--;
        this.navigateToPdfPage();
      }
    },
    navigateToPdfPage() {
      const pdfFrame = document.querySelector('.preview-pdf');
      if (pdfFrame) {
        // Try to access PDF viewer and navigate to the specific page
        try {
          // This works for browsers with built-in PDF viewers
          pdfFrame.contentWindow.postMessage({
            action: 'navigateToPage',
            page: this.pdfCurrentPage
          }, '*');
        } catch (e) {
          console.warn('Could not navigate PDF page programmatically:', e);
        }
      }
    },
    detectPdfPageCount() {
      // This is a best effort to detect PDF page count
      // Different browsers handle this differently
      const pdfFrame = document.querySelector('.preview-pdf');
      if (pdfFrame && pdfFrame.contentDocument) {
        try {
          // For browsers with PDF.js or similar viewers
          const pdfViewer = pdfFrame.contentDocument.querySelector('.pdfViewer');
          if (pdfViewer) {
            const pages = pdfViewer.querySelectorAll('.page');
            if (pages.length) {
              this.pdfTotalPages = pages.length;
              return;
            }
          }
          
          // Alternative way to detect
          const pageLabels = pdfFrame.contentDocument.querySelectorAll('.page-label');
          if (pageLabels.length > 0) {
            const lastPageLabel = pageLabels[pageLabels.length - 1];
            const match = lastPageLabel.textContent.match(/\d+/);
            if (match) {
              this.pdfTotalPages = parseInt(match[0], 10);
            }
          }
        } catch (e) {
          console.warn('Could not detect PDF page count:', e);
          // Default to a reasonable number if detection fails
          this.pdfTotalPages = 1;
        }
      }
    },
    requestScan(mail) {
      this.scanMail(mail);
    },
  }
}
</script>

<style scoped>
.mail-table-section {
  margin: 0;
  padding: 0;
}

.mail-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
}

.mail-table th,
.mail-table td {
  padding: 0.5rem 0.75rem;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
}

.mail-table th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
  font-size: 0.95rem;
}

.mail-table td {
  font-size: 0.95rem;
  color: #222;
}

.status-badge {
  display: inline-block;
  padding: 0.25em 0.75em;
  border-radius: 9999px;
  font-size: 0.85em;
  font-weight: 500;
  background: #f3f4f6;
  color: #555;
}

.status-badge.scanned {
  background: #ede9fe;
  color: #7c3aed;
}

.status-badge.received {
  background: #ecfdf5;
  color: #059669;
}

.status-badge.forwarded {
  background: #e0f2fe;
  color: #0284c7;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-right: 0.5rem;
  margin-bottom: 0;
  padding: 0.35rem 0.75rem;
  font-size: 0.95em;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
}

.action-btn.forward {
  background: #22c55e;
  color: #fff;
}

.action-btn.shredding {
  background: #ef4444;
  color: #fff;
}

.action-btn.scan-request {
  background: #10b981;
  color: #fff;
}

.action-btn.view {
  background: #3b82f6;
  color: #fff;
}

.action-btn i {
  margin-right: 0.25em;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.25rem;
  margin-top: 1rem;
  padding: 0.5rem;
}

.pagination button {
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #222;
  cursor: pointer;
  font-size: 0.95em;
}

.pagination button.active {
  background: #22c55e;
  color: #fff;
  border-color: #22c55e;
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mail-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.mail-controls {
  display: flex;
  gap: 0.5rem;
}

.view-toggle-btn {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  color: #374151;
  font-size: 1.1rem;
  padding: 0.35rem 0.7rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
}

.view-toggle-btn.active {
  background: #22c55e;
  color: #fff;
  border-color: #22c55e;
}

.mail-grid-section {
  margin: 0;
  padding: 0;
}

.mail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(270px, 1fr));
  gap: 1.25rem;
}

.mail-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  padding: 1.25rem 1rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.mail-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.mail-id {
  font-weight: 600;
  color: #374151;
  font-size: 1.05rem;
}

.mail-card-body {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.97rem;
  color: #222;
}

.mail-info-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.mail-info-row strong {
  min-width: 100px;
}

.mail-card-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  background: #fff;
  border-radius: 10px;
  margin: 1rem 0;
}

.loading-state i,
.empty-state i {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.loading-state i {
  color: #22c55e;
}

.empty-state i {
  font-size: 3rem;
  color: #9ca3af;
  margin-bottom: 1rem;
}

.empty-state p {
  margin: 0;
  font-size: 1.1rem;
}

.priority-badge {
  display: inline-block;
  padding: 0.25em 0.75em;
  border-radius: 9999px;
  font-size: 0.85em;
  font-weight: 500;
  margin-left: 0.5rem;
}

.priority-badge.low {
  background: #f3f4f6;
  color: #6b7280;
}

.priority-badge.medium {
  background: #e0f2fe;
  color: #0284c7;
}

.priority-badge.high {
  background: #fef3c7;
  color: #d97706;
}

.priority-badge.urgent {
  background: #fee2e2;
  color: #dc2626;
}

.action-btn.download {
  background: #3b82f6;
  color: #fff;
}

.status-text {
  color: #666;
  font-style: italic;
  font-size: 0.9em;
}

@media (max-width: 768px) {
  .mail-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .mail-card {
    padding: 1rem 0.75rem;
  }

  .mail-header-row {
    flex-direction: column;
    gap: 1rem;
  }

  .mail-controls {
    width: 100%;
    justify-content: space-between;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 16px;
  width: 95%;
  max-width: 1200px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: modal-appear 0.3s ease-out;
}

@keyframes modal-appear {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
}

.modal-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
}

.modal-header h2 i {
  color: #6b7280;
}

.modal-header .close-btn {
  background: #f3f4f6;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
}

.modal-header .close-btn:hover {
  background: #e5e7eb;
  color: #111827;
}

.modal-body {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.attachments-sidebar {
  width: 320px;
  border-right: 1px solid #e5e7eb;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.attachments-header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
}

.attachments-header h3 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.attachments-count {
  background: #e5e7eb;
  color: #4b5563;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
}

.attachments-list {
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
}

.attachment-item {
  padding: 0.75rem;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e5e7eb;
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.attachment-item:hover {
  border-color: #3b82f6;
  background: #f0f9ff;
}

.attachment-item.active {
  border-color: #3b82f6;
  background: #eff6ff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.attachment-item-header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.attachment-item-header i {
  font-size: 1.25rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.attachment-details {
  flex: 1;
  min-width: 0;
}

.attachment-name {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #6b7280;
}

.preview-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
}

.preview-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.preview-toolbar {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
}

.preview-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  font-weight: 500;
  color: #374151;
}

.preview-title i {
  color: #6b7280;
}

.preview-actions {
  display: flex;
  gap: 0.5rem;
}

.preview-btn {
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  font-size: 0.875rem;
}

.preview-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.preview-btn.download {
  background: #3b82f6;
  color: #fff;
  border: none;
}

.preview-btn.download:hover {
  background: #2563eb;
}

.preview-content {
  flex: 1;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #f8fafc;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.2s;
}

.preview-pdf {
  width: 100%;
  height: 100%;
  border: none;
}

.preview-unsupported,
.preview-placeholder,
.no-attachments {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: #6b7280;
  padding: 2rem;
  text-align: center;
}

.preview-unsupported i,
.preview-placeholder i,
.no-attachments i {
  font-size: 3rem;
  color: #9ca3af;
}

.preview-unsupported p,
.preview-placeholder p,
.no-attachments p {
  margin: 0;
  font-size: 1.1rem;
}

.preview-error-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  text-align: center;
}

.preview-error-content i {
  font-size: 2.5rem;
  color: #ef4444;
}

.preview-error-content p {
  margin: 0;
  color: #ef4444;
  font-weight: 500;
}

@media (max-width: 768px) {
  .modal-content {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }

  .attachments-sidebar {
    width: 280px;
  }
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.35rem 0.75rem;
  font-size: 0.95em;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
}

.action-btn.forward {
  background: #22c55e;
  color: #fff;
}

.action-btn.shredding {
  background: #ef4444;
  color: #fff;
}

.action-btn.scan-request {
  background: #10b981;
  color: #fff;
}

.action-btn.view {
  background: #3b82f6;
  color: #fff;
}

.action-btn:hover {
  opacity: 0.9;
}

.action-btn i {
  margin-right: 0.25em;
}

.status-text {
  color: #666;
  font-style: italic;
  font-size: 0.9em;
  padding: 0.35rem 0.75rem;
}

.pdf-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pdf-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.pdf-loading i {
  font-size: 2rem;
  color: #3b82f6;
  margin-bottom: 1rem;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.pdf-loading p {
  font-size: 1rem;
  color: #4b5563;
}

.pdf-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.75rem 1rem;
  border-top: 1px solid #e5e7eb;
  z-index: 1;
  transition: opacity 0.3s ease;
  display: flex;
  justify-content: center;
}

.pdf-pagination {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.pdf-page-info {
  font-size: 0.9rem;
  color: #4b5563;
  font-weight: 500;
}

.pdf-nav-btn {
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pdf-nav-btn:hover:not(:disabled) {
  background: #e5e7eb;
  color: #111827;
}

.pdf-nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>